import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch, fetchStageDashboard } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function StageTracker({ matterId, showNotification, bare = false, refreshTrigger = 0 }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingStage, setChangingStage] = useState(false);

  // Preview-stage state — clicking a non-current stage in the pipeline
  // bar SHOWS that stage's task-progress numbers without changing the
  // matter's actual stage. A separate "Switch to this stage" button is
  // required to actually advance. Substep 5-η.5.c: no more checklist
  // items in the response — just progress totals.
  const [previewStage, setPreviewStage] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // force:true — this tracker is the surface that shows the stage, so
  // it never wants a cached copy after something moved. Its siblings
  // mounting alongside it ride the cache this call fills.
  const loadDashboard = useCallback(() => {
    fetchStageDashboard(matterId, { force: true })
      .then(data => { setDashboard(data); setLoading(false); })
      .catch(e => { console.error("Stage dashboard load failed:", e); setLoading(false); });
  }, [matterId]);

  // Substep 5-η.5.f: refreshTrigger bumps when TasksCard toggles a
  // task so the progress bar reflects the change immediately, not
  // "next time the user visits this matter."
  useEffect(() => { loadDashboard(); }, [loadDashboard, refreshTrigger]);

  const handleStageChange = async (stageKey) => {
    setChangingStage(true);
    try {
      await apiFetch(`/api/matters/${matterId}/stage`, {
        method: "PUT",
        body: JSON.stringify({ stage: stageKey }),
      });
      showNotification("Stage updated successfully");
      setPreviewStage(null); // Once advanced, the new current IS the focus
      loadDashboard();
    } catch (e) {
      showNotification("Failed to update stage: " + e.message, "error");
    }
    setChangingStage(false);
  };

  // Pipeline click — preview stages by default, never auto-advance.
  // Clicking the current stage clears any preview.
  const handleStageClick = (stageKey) => {
    if (changingStage) return;
    const currentKey = dashboard?.current_stage;
    if (stageKey === currentKey) {
      setPreviewStage(null);
    } else {
      setPreviewStage(stageKey === previewStage ? null : stageKey);
    }
  };

  // Fetch preview-stage progress when previewStage changes
  useEffect(() => {
    const currentKey = dashboard?.current_stage;
    if (!previewStage || previewStage === currentKey) {
      setPreviewProgress(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    apiFetch(`/api/matters/${matterId}/stage/progress?stage=${previewStage}`)
      .then(d => {
        if (cancelled) return;
        setPreviewProgress(d);
        setPreviewLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        console.error("Stage preview load failed:", e);
        setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [previewStage, dashboard?.current_stage, matterId]);

  // Substep 5-η.5.d: bare=true drops background/border/marginBottom
  // so the caller can embed this inside a shared card. Padding is
  // preserved — the content still needs internal breathing room; the
  // shared card provides the outer frame + border, not padding.
  const outerStyle = bare
    ? { padding: "20px 24px 12px" }
    : { background:"#fff", borderRadius:"12px", padding:"24px 28px",
        border:`1px solid ${C.warmGray}`, marginBottom:"20px" };

  if (loading) {
    return React.createElement("div", { style: outerStyle },
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } }, "Loading stage tracker..."),
      ));
  }

  if (!dashboard) return null;

  const stages = dashboard.stages || [];
  const progress = dashboard.progress || {};
  const currentKey = dashboard.current_stage;

  // Display variables — when previewing a non-current stage, the panel
  // below the pipeline reflects THAT stage's data. When not previewing
  // (or previewing the current stage), show the actual current.
  const isPreviewing = !!(previewStage && previewStage !== currentKey);
  const displayKey = isPreviewing ? previewStage : currentKey;
  const displayProgress = isPreviewing ? (previewProgress || {}) : progress;
  const displayStageInfo = stages.find(s => s.key === displayKey) || {};
  const previewStageInfo = stages.find(s => s.key === previewStage) || {};

  // Substep 5-η.5.e: "Current: X" label above the pipeline so
  // paralegals see the stage name at a glance without having to scan
  // the pipeline cells. Only rendered when we know the current stage.
  const currentStageInfoInline = stages.find(s => s.key === currentKey);
  const currentLabelBar = currentStageInfoInline && React.createElement("div", {
    style: {
      display: "flex", alignItems: "baseline", gap: "8px",
      marginBottom: "8px",
    }
  },
    React.createElement("span", {
      style: {
        fontSize: "10px", color: C.textMuted, fontWeight: 600,
        letterSpacing: "0.06em", textTransform: "uppercase",
      }
    }, "Current"),
    React.createElement("span", {
      style: {
        fontSize: "14px", color: currentStageInfoInline.color, fontWeight: 700,
      }
    }, currentStageInfoInline.label),
  );

  // Stage pipeline bar
  const pipelineBar = React.createElement("div", {
    style: { display:"flex", gap:"2px", marginBottom:"20px", position:"relative" }
  },
    stages.map((s, i) => {
      const isCurrent = s.key === currentKey;
      const isCompleted = s.status === "completed";
      const isPreviewed = s.key === previewStage && previewStage !== currentKey;
      const taskTotal = s.task_count || 0;
      const taskDone = s.task_completed || 0;
      const docs = s.template_count || 0;

      return React.createElement("button", {
        key: s.key,
        onClick: () => handleStageClick(s.key),
        disabled: changingStage,
        title: isCurrent
          ? `${s.label} (current stage)`
          : `${s.label}: ${s.description} — click to preview`,
        style: {
          // Substep 5-η.5.e: current stage cell is 1.6x wider for visual
          // dominance. Others share the remaining flex space equally.
          flex: isCurrent ? 1.6 : 1,
          padding: isCurrent ? "12px 8px" : (isPreviewed ? "10px 8px" : "9px 6px"),
          border: isCurrent
            ? `2px solid ${s.color}`
            : isPreviewed
              ? `2px dashed ${s.color}`
              : `1px solid ${isCompleted ? s.color + "60" : C.warmGray}`,
          borderRadius: i === 0 ? "10px 0 0 10px" : i === stages.length - 1 ? "0 10px 10px 0" : "0",
          background: isCurrent
            ? s.color + "22"                       // stronger fill so current pops
            : isPreviewed
              ? s.color + "08"
              : isCompleted ? s.color + "10" : "#fff",
          cursor: changingStage ? "wait" : "pointer",
          textAlign: "center",
          transition: "all 0.2s ease",
          position: "relative",
          outline: "none",
        }
      },
        // Stage label
        React.createElement("div", {
          style: {
            fontSize: isCurrent ? "13px" : "11px",
            fontWeight: isCurrent ? 700 : 600,
            color: isCurrent ? s.color : isCompleted ? s.color : C.textLight,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }
        }, s.label),
        // Task + doc counts. Only show the milestone-tasks pair when
        // there ARE any; otherwise just show docs (cleaner for stages
        // the firm hasn't tagged milestone templates for yet).
        React.createElement("div", {
          style: {
            fontSize:"10px", color: isCurrent ? s.color + "cc" : C.textLight,
            marginTop:"3px", fontWeight: isCurrent ? 600 : 400,
          }
        },
          taskTotal > 0 ? `${taskDone}/${taskTotal} tasks · ${docs} docs` : `${docs} docs`,
        ),
        // Current indicator dot
        isCurrent && React.createElement("div", {
          style: {
            width:"6px", height:"6px", borderRadius:"50%", background:s.color,
            margin:"5px auto 0", transition:"all 0.2s",
          }
        }),
        // Completed checkmark
        isCompleted && React.createElement("div", {
          style: { marginTop:"3px" }
        }, React.createElement("svg", {width:12,height:12,viewBox:"0 0 24 24",fill:"none",stroke:s.color,strokeWidth:3,strokeLinecap:"round"},
          React.createElement("polyline", {points:"20 6 9 17 4 12"})
        )),
      );
    }),
  );

  // Preview banner — shown when previewing a non-current stage. Read-only:
  // stage transitions happen in Clio's Matter Stage field (Details tab),
  // not in this app. The banner tells the user what they're looking at
  // and points them at Clio for actually advancing.
  const currentStageInfo = stages.find(s => s.key === currentKey) || {};
  const previewBanner = isPreviewing && React.createElement("div", {
    style: {
      marginBottom:"16px", padding:"12px 14px", borderRadius:"10px",
      background: (previewStageInfo.color || C.gold) + "10",
      border: `1px dashed ${previewStageInfo.color || C.gold}80`,
      display:"flex", justifyContent:"space-between", alignItems:"center", gap:"10px",
    }
  },
    React.createElement("div", null,
      React.createElement("div", { style: { fontSize:"12px", fontWeight:700, color:C.navy } },
        `Previewing: ${previewStageInfo.label || previewStage}`,
      ),
      React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, marginTop:"2px" } },
        `Current stage is ${currentStageInfo.label || currentKey}. To advance, update Matter Stage on the Details tab in Clio.`,
      ),
    ),
    React.createElement("button", {
      onClick: () => setPreviewStage(null),
      style: {
        padding:"6px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`,
        background:"#fff", color:C.textMuted, fontSize:"11px", fontWeight:600, cursor:"pointer",
        flexShrink:0,
      }
    }, "Back to current"),
  );

  // Progress bar for current OR previewed stage.
  //
  // Substep 5-η.5.c UX: distinguish "no milestones exist yet for this
  // stage on this matter" from "all milestones done". The old shape
  // returned `total: 0, percentage: 100` for both, which read as
  // "everything's done!" when it really means "nothing's been set up
  // yet." Now the empty case renders explicitly.
  const pct = displayProgress.percentage || 0;
  const totalCount = displayProgress.total || 0;
  const isLoadingPreview = isPreviewing && previewLoading;
  const hasNoMilestones = !isLoadingPreview && totalCount === 0;
  const progressBar = React.createElement("div", { style: { marginBottom:"16px" } },
    React.createElement("div", {
      style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }
    },
      React.createElement("span", {
        style: {
          fontSize:"12px",
          fontWeight: hasNoMilestones ? 500 : 600,
          color: hasNoMilestones ? C.textMuted : C.navy,
          fontStyle: hasNoMilestones ? "italic" : "normal",
        }
      }, isLoadingPreview
        ? "Loading preview..."
        : hasNoMilestones
          ? "No milestones tagged for this stage yet"
          : `${displayProgress.completed || 0} of ${totalCount} required items complete`,
      ),
      // Percentage badge suppressed when there are no milestones —
      // "100%" against "0 of 0" was the confusing part.
      !hasNoMilestones && !isLoadingPreview && React.createElement("span", {
        style: { fontSize:"12px", fontWeight:700, color: displayStageInfo.color || C.gold }
      }, pct + "%"),
    ),
    // Empty progress track when no milestones — no fill, just the rail
    React.createElement("div", {
      style: { height:"6px", borderRadius:"3px", background:C.warmGray + "60", overflow:"hidden" }
    },
      !hasNoMilestones && React.createElement("div", {
        style: {
          height:"100%", borderRadius:"3px",
          background: `linear-gradient(90deg, ${displayStageInfo.color || C.gold}, ${(displayStageInfo.color || C.gold)}CC)`,
          width: pct + "%",
          transition: "width 0.5s ease",
        }
      }),
    ),
  );

  // Substep 5-η.5.c: checklist rendering deleted. Milestone tasks
  // live in TasksCard where they're grouped by stage — clicking to
  // check them off happens there, not here. StageTracker now just
  // shows the pipeline + progress bar + "advance in Clio" hint.

  return React.createElement("div", { style: outerStyle },
    // Header
    React.createElement("div", {
      style: { marginBottom:"16px" }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, margin:0 }
      }, "Case Stage"),
    ),

    // "Current: X" label (5-η.5.e)
    currentLabelBar,

    // Pipeline bar
    pipelineBar,

    // Preview banner (only when previewing a non-current stage)
    previewBanner,

    // Progress
    progressBar,

    // Persistent hint — stage transitions don't happen in this app;
    // they happen in Clio's Matter Stage field on the Details tab.
    // Previously this guidance was only visible when previewing a
    // non-current stage (via the previewBanner). New staff hit this
    // confusion repeatedly — checking off items here didn't "advance"
    // because there's no advance button. Always-visible hint at the
    // bottom of the pipeline section makes the workflow explicit.
    React.createElement("div", {
      style: {
        marginTop: "10px", padding: "8px 12px",
        borderRadius: "8px", background: `${C.gold}10`,
        border: `1px solid ${C.gold}30`,
        fontSize: "11px", color: C.textMuted, lineHeight: 1.5,
        display: "flex", alignItems: "center", gap: "8px",
      },
    },
      React.createElement("svg", {
        "aria-hidden": "true", width: 13, height: 13, viewBox: "0 0 24 24",
        fill: "none", stroke: C.gold, strokeWidth: 2.5, strokeLinecap: "round",
        style: { flexShrink: 0 },
      },
        React.createElement("circle", { cx: 12, cy: 12, r: 10 }),
        React.createElement("line", { x1: 12, y1: 8, x2: 12, y2: 12 }),
        React.createElement("line", { x1: 12, y1: 16, x2: 12.01, y2: 16 }),
      ),
      React.createElement("span", null,
        "To advance the matter to the next stage, open the matter in Clio and update the ",
        React.createElement("strong", { style: { color: C.navy, fontWeight: 600 } }, "Matter Stage"),
        " field on the Details tab.",
      ),
    ),

  );
}


